import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Heart, Eye, ShoppingCart, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Toast } from '@/components/ui/toast'
import type { Video } from '@/types/database'

interface VideoCardProps {
  video: Video & {
    profiles: { full_name: string | null } | null
    products: { title: string } | null
  }
}

export function VideoCard({ video }: VideoCardProps) {
  const user = useAuthStore((state) => state.user)
  const [likes, setLikes] = useState(video.likes_count || 0)
  const [isLiked, setIsLiked] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [viewCount, setViewCount] = useState(0)

  useEffect(() => {
    if (user) {
      checkIfLiked()
    }
    fetchViewCount()
  }, [user, video.id])

  const checkIfLiked = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('video_likes')
        .select('id')
        .eq('video_id', video.id)
        .eq('user_id', user.id)
        .maybeSingle()

      if (error) throw error
      setIsLiked(!!data)
    } catch (err) {
      console.error('Error checking like status:', err)
    }
  }

  const fetchViewCount = async () => {
    try {
      const { data, error } = await supabase
        .from('video_views')
        .select('count')
        .eq('video_id', video.id)
        .maybeSingle()

      if (error) throw error
      setViewCount(data?.count || 0)
    } catch (err) {
      console.error('Error fetching view count:', err)
    }
  }

  const handleLike = async () => {
    if (!user) {
      setToastMessage('Veuillez vous connecter pour aimer cette vidéo')
      setToastType('error')
      setShowToast(true)
      return
    }

    try {
      setLoading(true)

      if (isLiked) {
        // Unlike the video
        const { error } = await supabase
          .from('video_likes')
          .delete()
          .eq('video_id', video.id)
          .eq('user_id', user.id)

        if (error) throw error

        // Update video likes count
        await supabase
          .from('videos')
          .update({ likes_count: Math.max(0, likes - 1) })
          .eq('id', video.id)

        setLikes(prev => Math.max(0, prev - 1))
        setIsLiked(false)
      } else {
        // Like the video
        const { error } = await supabase
          .from('video_likes')
          .insert([{ video_id: video.id, user_id: user.id }])

        if (error) throw error

        // Update video likes count
        await supabase
          .from('videos')
          .update({ likes_count: likes + 1 })
          .eq('id', video.id)

        setLikes(prev => prev + 1)
        setIsLiked(true)
      }
    } catch (err) {
      console.error('Error updating like:', err)
      setToastMessage('Une erreur est survenue')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const incrementViews = async () => {
    try {
      // First check if the video_views record exists
      const { data, error } = await supabase
        .from('video_views')
        .select('count')
        .eq('video_id', video.id)
        .maybeSingle()

      if (error) throw error

      if (data) {
        // Update existing record
        const newCount = data.count + 1
        await supabase
          .from('video_views')
          .update({ count: newCount })
          .eq('video_id', video.id)
        
        setViewCount(newCount)
      } else {
        // Create new record with upsert to handle conflicts
        await supabase
          .from('video_views')
          .upsert([{ video_id: video.id, count: 1 }])
        
        setViewCount(1)
      }
    } catch (err) {
      console.error('Error incrementing views:', err)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md border-2 border-black overflow-hidden">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
      
      <div className="aspect-video relative">
        <img
          src={video.thumbnail_url || 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80'}
          alt={video.title}
          className="w-full h-full object-cover"
        />
        <Link
          to={`/videos/${video.id}`}
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity"
          onClick={incrementViews}
        >
          <Eye className="h-12 w-12 text-white" />
        </Link>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
              <Link to={`/videos/${video.id}`}>{video.title}</Link>
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              By {video.profiles?.full_name || 'Anonymous'}
            </p>
          </div>
          <button
            onClick={handleLike}
            disabled={loading}
            className={`p-2 rounded-full ${
              isLiked
                ? 'text-red-500 bg-red-50 border-2 border-red-200'
                : 'text-gray-400 hover:text-red-500 hover:bg-red-50 border-2 border-gray-200'
            }`}
          >
            <Heart className={`h-5 w-5 ${loading ? 'animate-pulse' : ''}`} />
          </button>
        </div>

        {video.products && (
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-500">Produit associé:</span>
            <Link to={`/products/${video.products.id}`}>
              <Button
                variant="outline"
                size="sm"
                className="border-2 border-black hover:border-primary"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Voir le produit
              </Button>
            </Link>
          </div>
        )}

        <div className="mt-4 flex items-center text-sm text-gray-500">
          <Eye className="h-4 w-4 mr-1" />
          <span className="mr-4">{viewCount} vues</span>
          <Heart className="h-4 w-4 mr-1" />
          <span>{likes}</span>
          <MessageSquare className="h-4 w-4 ml-4 mr-1" />
          <span>0</span>
        </div>
      </div>
    </div>
  )
}